import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for the Takata mystery-call list.
 *
 * The grid itself does not go through here — it uses `useServerDataGrid`, which
 * owns the paging state and posts to `POST query`. What is left is the three
 * supporting reads (bootstrap, filter options, call log) and the export URL.
 */

const BASE = '/api/stla/takata/calls';

/**
 * The wave value meaning "do not filter by wave".
 *
 * Mirrors `DashboardDataService.AllWaves`. It has to be a real value rather than
 * an omitted parameter, because omitting the wave means "the most recent one".
 */
export const ALL_WAVES = 'all';

export interface TakataCallRow {
  /** The call record's primary key — unique per row, unlike the pair below. */
  callId: number;
  physOut: string;
  actorCode: string;
  companyName: string;
  brand: string;
  town: string;
  address: string;
  calledPhoneNumber: string;
  wave: string;
  quarter: string | null;
  outcomeDate: string | null;
  status: string | null;
  appointmentDate: string | null;
  delay: number | null;
  outcomeDetails: string | null;
  campaignEntryId: number | null;
  uniqueContactId: string | null;
  lastUpdate: string | null;
  lastCall: boolean;
}

export interface DealerIdentity {
  companyName: string;
  vatCode: string;
}

export interface TakataCallsBootstrap {
  availableWaves: string[];
  selectedWave: string | null;
  totalCalls: number;
  dealerIdentities: DealerIdentity[];
  /**
   * The waves whose calls expand their log and may be exported — the subset of
   * `availableWaves` that passes `TakataWavePolicy` on the server.
   *
   * A list rather than a cutoff on purpose: wave ids are vendor strings like
   * `2026_Wave8`, which do not sort as plain strings (`2026_Wave10` < `2026_Wave8`),
   * so the client tests membership and the ordering rule stays server-side.
   */
  callLogWaves: string[];
}

export interface TakataCallsFilterOptions {
  brands: string[];
  quarters: string[];
  statuses: string[];
  outcomeDetails: string[];
}

/** Filter bag posted with every grid query; also what the export URL carries. */
export type TakataCallFilters = Record<string, string | undefined>;

export const takataCallKeys = {
  all: ['stla', 'takata-calls'] as const,
  bootstrap: (wave: string | null) => ['stla', 'takata-calls', 'bootstrap', wave] as const,
  filterOptions: (wave: string | null) => ['stla', 'takata-calls', 'filter-options', wave] as const,
  grid: ['stla', 'takata-calls', 'grid'] as const,
  log: (campaignEntryId: number, wave: string | null) =>
    ['stla', 'takata-calls', 'log', campaignEntryId, wave] as const,
};

/** Waves, the selected one, the header total and any dealer identities. */
export function useTakataCallsBootstrap(wave: string | null) {
  return useQuery<TakataCallsBootstrap>({
    queryKey: takataCallKeys.bootstrap(wave),
    queryFn: () => api.get<TakataCallsBootstrap>(`${BASE}/bootstrap`, { query: { wave } }),
  });
}

/** Distinct values behind the Brand / Quarter / Status / Outcome dropdowns. */
export function useTakataCallsFilterOptions(wave: string | null) {
  return useQuery<TakataCallsFilterOptions>({
    queryKey: takataCallKeys.filterOptions(wave),
    queryFn: () => api.get<TakataCallsFilterOptions>(`${BASE}/filter-options`, { query: { wave } }),
    enabled: wave != null,
  });
}

/**
 * Every attempt for one campaign entry, not just the final one the grid shows.
 *
 * Fetched only once a row is actually expanded, and then cached by the query
 * client — which is what the Razor page hand-rolled by stashing the rendered
 * HTML on the row so a second expand did not re-request.
 */
export function useTakataCallLog(campaignEntryId: number | null, wave: string | null) {
  return useQuery<TakataCallRow[]>({
    queryKey: takataCallKeys.log(campaignEntryId ?? -1, wave),
    queryFn: () =>
      api.get<TakataCallRow[]>(`${BASE}/${campaignEntryId}/log`, { query: { wave } }),
    enabled: campaignEntryId != null,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Builds the export URL for the current filters and sort.
 *
 * The filter bag goes out as `filters[key]=value`, which is how ASP.NET binds a
 * `Dictionary<string, string>` from the query string — the same bag the grid
 * posts, so the file matches what is on screen.
 */
export function buildExportUrl(
  filters: TakataCallFilters,
  search?: string,
  sortBy?: string,
  sortDir?: string,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(`filters[${key}]`, value);
  }
  if (search) params.set('search', search);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDir) params.set('sortDir', sortDir);
  return `${BASE}/export?${params.toString()}`;
}
